import { Download, Loader2, Upload, X } from 'lucide-react'
import { useRef, useState } from 'react'

import { bulkUploadCustomers } from '@/modules/customer/api/customer.api'
import { useCustomerStore } from '@/modules/customer/store/customer.store'
import { downloadCustomerTemplate } from '@/modules/customer/utils/customerTemplate'
import { lookupPincode } from '@/shared/api/pincode.api'
import { toast } from '@/shared/lib/toast'

const INITIAL_FORM = {
  companyName: '',
  contactPersonName: '',
  contactPersonPhone: '',
  contactPersonEmail: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  pincode: '',
  country: '',
  gst: '',
}

const sanitizeCustomerInput = (field, value) => {
  if (field === 'companyName') {
    return value.replace(/[^a-zA-Z0-9\s.,&'()/-]/g, '')
  }
  if (field === 'contactPersonName') {
    return value.replace(/[^a-zA-Z0-9\s.'-]/g, '')
  }
  if (field === 'contactPersonPhone') {
    return value.replace(/\D/g, '').slice(0, 10)
  }
  if (
    field === 'addressLine1' ||
    field === 'addressLine2' ||
    field === 'city' ||
    field === 'state' ||
    field === 'country'
  ) {
    return value.replace(/[^a-zA-Z0-9\s.,#&'()/-]/g, '')
  }
  if (field === 'pincode') {
    return value.replace(/\D/g, '').slice(0, 6)
  }
  if (field === 'gst') {
    return value
      .replace(/[^a-zA-Z0-9]/g, '')
      .slice(0, 15)
      .toUpperCase()
  }
  return value
}

const validateCustomerForm = (data) => {
  if (!data.companyName?.trim()) {
    return 'Company Name is required'
  }
  if (!/^[a-zA-Z0-9\s.,&'()/-]+$/.test(data.companyName.trim())) {
    return 'Company Name must be alphanumeric'
  }
  if (!data.contactPersonName?.trim()) {
    return 'Contact Person Name is required'
  }
  if (!/^[a-zA-Z0-9\s.'-]+$/.test(data.contactPersonName.trim())) {
    return 'Contact Person Name must be alphanumeric'
  }
  if (!data.contactPersonPhone?.trim()) {
    return 'Contact Person Phone is required'
  }
  if (!/^\d{10}$/.test(data.contactPersonPhone.trim())) {
    return 'Phone number must be numeric and exactly 10 digits'
  }
  if (!data.contactPersonEmail?.trim()) {
    return 'Contact Person Email is required'
  }
  if (
    !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(
      data.contactPersonEmail.trim(),
    )
  ) {
    return 'Please enter a valid email address'
  }
  if (!data.addressLine1?.trim()) return 'Address Line 1 is required'
  if (!data.city?.trim()) return 'City is required'
  if (!data.state?.trim()) return 'State is required'
  if (!data.pincode?.trim()) return 'Pincode is required'
  if (!data.country?.trim()) return 'Country is required'
  if (data.gst && data.gst.trim() && !/^[a-zA-Z0-9]+$/.test(data.gst.trim())) {
    return 'GST must be alphanumeric'
  }
  return null
}

export function AddCustomerModal({ open, onOpenChange, onCustomerCreated }) {
  const [customerForm, setCustomerForm] = useState(INITIAL_FORM)
  const [isBulkUploading, setIsBulkUploading] = useState(false)
  const [isLookingUpPincode, setIsLookingUpPincode] = useState(false)
  const bulkFileInputRef = useRef(null)

  const { isSubmitting, createCustomer, fetchCustomers } = useCustomerStore()

  const handleChange = (field, value) => {
    setCustomerForm((prev) => ({
      ...prev,
      [field]: sanitizeCustomerInput(field, value),
    }))
  }

  const handlePincodeChange = async (e) => {
    const rawVal = e.target.value
    const pin = rawVal.replace(/\D/g, '').slice(0, 6)

    setCustomerForm((prev) => ({
      ...prev,
      pincode: pin,
    }))

    if (pin.length === 6) {
      try {
        setIsLookingUpPincode(true)
        const details = await lookupPincode(pin)
        if (details) {
          setCustomerForm((prev) => ({
            ...prev,
            pincode: pin,
            city: details.city || prev.city,
            state: details.state || prev.state,
            country: details.country || prev.country || 'India',
          }))
          toast.success(
            `Location auto-filled: ${details.city}, ${details.state}`,
          )
        } else {
          toast.error('No location found for this pincode')
        }
      } catch (err) {
        console.error('Pincode lookup error:', err)
        toast.error('Failed to fetch location details for this pincode')
      } finally {
        setIsLookingUpPincode(false)
      }
    }
  }

  const handleCreateCustomer = async () => {
    const errorMsg = validateCustomerForm(customerForm)
    if (errorMsg) {
      toast.error(errorMsg)
      return
    }

    try {
      const result = await createCustomer(customerForm)

      /*
       * createCustomer already returns the API response.
       *
       * Support the possible response shapes without changing
       * your existing API/store implementation.
       */
      const createdCustomer =
        result?.data?.customer || result?.data || result?.customer || null

      if (onCustomerCreated) {
        onCustomerCreated(createdCustomer)
      }

      setCustomerForm(INITIAL_FORM)
      onOpenChange(false)
    } catch {
      // Error is already handled by the customer store
    }
  }

  const handleBulkUpload = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    setIsBulkUploading(true)
    try {
      const result = await bulkUploadCustomers(file)
      const data = result?.data
      const count = data?.count ?? 0
      if (count > 0)
        toast.success(`Successfully uploaded ${count} customer(s)!`)
      if (data?.errors?.length) {
        data.errors
          .slice(0, 3)
          .forEach((e) => toast.error(`Row ${e.row}: ${e.message}`))
        if (data.errors.length > 3) {
          toast.error(`…and ${data.errors.length - 3} more row error(s).`)
        }
      }
      // Refresh the customer list
      fetchCustomers({ page: 1 })
      if (count > 0) {
        onOpenChange(false)
      }
    } catch (err) {
      toast.error(err?.message || 'Bulk upload failed')
    } finally {
      setIsBulkUploading(false)
      event.target.value = ''
    }
  }

  const handleClose = () => {
    if (isSubmitting) return

    setCustomerForm(INITIAL_FORM)
    onOpenChange(false)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-3 sm:p-4">
      <div className="w-full max-w-2xl rounded-2xl sm:rounded-3xl bg-white p-4 sm:p-7 shadow-xl max-h-[92vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              Add Customer
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Add a new customer or bulk upload via Excel.
            </p>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="flex h-9 w-9 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Bulk Upload Section */}
        <div className="mt-5 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-4">
          <p className="text-xs font-semibold text-slate-600 mb-3">
            Bulk Upload via Excel / CSV
          </p>
          <div className="flex flex-col sm:flex-row gap-2.5">
            {/* Hidden file input */}
            <input
              ref={bulkFileInputRef}
              type="file"
              accept=".csv,.xls,.xlsx"
              className="hidden"
              onChange={handleBulkUpload}
            />
            <button
              type="button"
              disabled={isBulkUploading}
              onClick={() => bulkFileInputRef.current?.click()}
              className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50 w-full sm:w-auto"
            >
              {isBulkUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 text-slate-500" />
              )}
              {isBulkUploading ? 'Uploading...' : 'Upload Excel / CSV'}
            </button>
            <button
              type="button"
              onClick={downloadCustomerTemplate}
              className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 w-full sm:w-auto"
            >
              <Download className="h-4 w-4 text-slate-500" />
              Download Template
            </button>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">
            Upload multiple customers at once. Download the template to see the
            required format.
          </p>
        </div>

        {/* Divider */}
        <div className="mt-5 flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs font-medium text-slate-400">
            or add manually
          </span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        {/* Customer Form */}
        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
          {/* Company Name */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Company Name
            </label>

            <input
              type="text"
              value={customerForm.companyName}
              onChange={(e) => handleChange('companyName', e.target.value)}
              placeholder="Enter company name"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
            />
          </div>

          {/* Contact Person Name */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Contact Person Name
            </label>

            <input
              type="text"
              value={customerForm.contactPersonName}
              onChange={(e) =>
                handleChange('contactPersonName', e.target.value)
              }
              placeholder="Enter contact person name"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
            />
          </div>

          {/* Contact Person Phone Number */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Contact Person Phone Number
            </label>

            <input
              type="tel"
              maxLength={10}
              value={customerForm.contactPersonPhone}
              onChange={(e) =>
                handleChange('contactPersonPhone', e.target.value)
              }
              placeholder="Enter phone number"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
            />
          </div>

          {/* Contact Person Email ID */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Contact Person Email ID
            </label>

            <input
              type="email"
              value={customerForm.contactPersonEmail}
              onChange={(e) =>
                handleChange('contactPersonEmail', e.target.value)
              }
              placeholder="Enter email address"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
            />
          </div>

          {/* Address Line 1 */}
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Address Line 1
            </label>
            <input
              type="text"
              value={customerForm.addressLine1}
              onChange={(e) => handleChange('addressLine1', e.target.value)}
              placeholder="Enter address line 1"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
            />
          </div>

          {/* Address Line 2 */}
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Address Line 2
              <span className="ml-1 text-xs font-normal text-slate-400">
                (Optional)
              </span>
            </label>
            <input
              type="text"
              value={customerForm.addressLine2}
              onChange={(e) => handleChange('addressLine2', e.target.value)}
              placeholder="Enter address line 2"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
            />
          </div>

          {/* City */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              City
            </label>
            <input
              type="text"
              value={customerForm.city}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="Enter city"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
            />
          </div>

          {/* State */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              State
            </label>
            <input
              type="text"
              value={customerForm.state}
              onChange={(e) => handleChange('state', e.target.value)}
              placeholder="Enter state"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
            />
          </div>

          {/* Pincode */}
          <div>
            <label className="mb-2 flex items-center justify-between text-sm font-medium text-slate-700">
              <span>Pincode</span>
              {isLookingUpPincode && (
                <span className="flex items-center gap-1 text-xs font-normal text-blue-600">
                  <Loader2 className="size-3 animate-spin" />
                  Fetching...
                </span>
              )}
            </label>
            <div className="relative">
              <input
                type="text"
                maxLength={6}
                value={customerForm.pincode}
                onChange={handlePincodeChange}
                placeholder="Enter 6-digit pincode"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
              />
              {isLookingUpPincode && (
                <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
                  <Loader2 className="size-4 animate-spin text-slate-400" />
                </div>
              )}
            </div>
          </div>

          {/* Country */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Country
            </label>
            <input
              type="text"
              value={customerForm.country}
              onChange={(e) => handleChange('country', e.target.value)}
              placeholder="Enter country"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-slate-400"
            />
          </div>

          {/* GST */}
          <div className="md:col-span-2">
            <label className="mb-2 block text-sm font-medium text-slate-700">
              GST
              <span className="ml-1 text-xs font-normal text-slate-400">
                (Optional)
              </span>
            </label>

            <input
              type="text"
              maxLength={15}
              value={customerForm.gst}
              onChange={(e) => handleChange('gst', e.target.value)}
              placeholder="Enter GST number if available"
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm uppercase outline-none focus:border-slate-400"
            />
          </div>
        </div>

        {/* Modal Actions */}
        <div className="mt-7 flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5 sm:gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto rounded-full border border-slate-200 px-6 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleCreateCustomer}
            disabled={isSubmitting}
            className="w-full sm:w-auto rounded-full bg-slate-950 px-7 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? 'Adding...' : 'Add Customer'}
          </button>
        </div>
      </div>
    </div>
  )
}
